import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { audienceService } from '../../services';
import { useDialog } from '../../context/DialogContext';
import { ClubPanel, PanelHead, PillButton, RoundCheck, TagPill, Face, useArmed, useToast, Toast, INK, MUTED, SUBTLE, BODY, INSET, CHIP, RED, RED_TINT, RULE, label as lbl, inputStyle } from '../club/clubUi';
import BrandedQR from '../BrandedQR';
import LoadingSpinner from '../LoadingSpinner';
import { brandedQrBlob } from '../../utils/brandedQr';
import ImportSheet from './ImportSheet';

const FILTERS = [
  { key: '', label: 'ALL' },
  { key: 'email', label: 'EMAIL' },
  { key: 'phone', label: 'PHONE' },
  { key: 'claimed', label: 'ON PORTAL' },
  { key: 'unclaimed', label: 'NOT YET' },
  { key: 'link', label: 'FROM THE LINK' },
  { key: 'import', label: 'IMPORTED' },
];
const SOURCE_LABEL = { link: 'LINK', import: 'IMPORTED', manual: 'ADDED', follow: 'FOLLOWER' };
const REQ_NOTES = {
  either: 'Either is the easy door. Asking for both means every contact can be reached by text and by email.',
  email: 'Only an email gets them in. Good if you mostly send email.',
  phone: 'Only a phone number gets them in. Good if you mostly text.',
  both: 'Every contact can be reached by text and by email. A higher bar, so fewer people finish.',
};

const chip = (on, bg = CHIP) => ({ display: 'inline-flex', alignItems: 'center', gap: 8, height: 36, padding: '0 14px', border: 'none', borderRadius: 999, background: on ? INK : bg, color: on ? '#fff' : INK, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 });

/**
 * CONTACTS (design_handoff_club_pages/README.md §2) — the people tab on a club's / Pro page's
 * manage surface: YOUR LINK (+ QR, the join-page line, what a joiner has to give), the stats,
 * the importer and add-by-hand, the groups, and THE LIST with search, filters, select-all and
 * the black bulk bar. `owner` is { ownerType, ownerId, name, publicPath, followerWord };
 * `onSendTo({ userIds, contactIds })` (a club passes it) powers SEND TO THESE →.
 */
export default function ContactsPanel({ owner, mobile = false, onSendTo = null }) {
  const { ownerType, ownerId } = owner;
  const { showAlert } = useDialog();
  const { armed, confirm, disarm, arm } = useArmed(6000);
  const { toast, say } = useToast();

  const [link, setLink] = useState(null);
  const [groups, setGroups] = useState([]);
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('');
  const [groupId, setGroupId] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(() => new Set());
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [headline, setHeadline] = useState('');
  const [headlineSaved, setHeadlineSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [groupEdit, setGroupEdit] = useState(null); // { id?, name }
  const searchTimer = useRef(null);

  const loadGroups = useCallback(async () => {
    const r = await audienceService.listGroups(ownerType, ownerId);
    if (r.success) setGroups(r.data || []);
  }, [ownerType, ownerId]);

  const loadList = useCallback(async (opts = {}) => {
    const p = opts.page || 1;
    const r = await audienceService.listContacts(ownerType, ownerId, { q: q || undefined, filter: filter || undefined, groupId: groupId || undefined, page: p, pageSize: 50 });
    if (!r.success) { setList((l) => l || { items: [], total: 0, counts: {} }); return; }
    setList((prev) => (p > 1 && prev ? { ...r.data, items: [...prev.items, ...r.data.items] } : r.data));
    setPage(p);
  }, [ownerType, ownerId, q, filter, groupId]);

  useEffect(() => {
    let live = true;
    audienceService.getLink(ownerType, ownerId).then((r) => { if (live && r.success) { setLink(r.data); setHeadline(r.data.headline || ''); } });
    loadGroups();
    return () => { live = false; };
  }, [ownerType, ownerId, loadGroups]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => loadList(), q ? 220 : 0);
    return () => clearTimeout(searchTimer.current);
  }, [loadList, q]);

  const refresh = () => { loadList(); loadGroups(); setSelected(new Set()); disarm(); };

  // ── The link ──
  const copy = async () => {
    if (!link) return;
    try { await navigator.clipboard.writeText(link.url); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* clipboard blocked */ }
  };
  const share = async () => {
    if (!link) return;
    if (navigator.share) { try { await navigator.share({ title: owner.name, text: `Get on ${owner.name}'s list`, url: link.url }); } catch { /* dismissed */ } }
    else copy();
  };
  const downloadQr = async () => {
    if (!link) return;
    try {
      const blob = await brandedQrBlob(link.url, 1024);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${owner.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-join-qr.png`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { await showAlert({ title: 'COULD NOT MAKE THE QR', message: 'Screenshot it instead.', type: 'error' }); }
  };
  const rotate = async () => {
    const r = await audienceService.rotateLink(ownerType, ownerId);
    disarm();
    if (r.success) { setLink(r.data); say('NEW LINK · THE OLD ONE STOPPED WORKING'); }
  };
  // WHAT THEY GIVE: either (default) · email · phone · both. Saved on tap.
  const setRequire = async (requireEmail, requirePhone) => {
    const r = await audienceService.setLinkRequirements(ownerType, ownerId, { requireEmail, requirePhone });
    if (r.success) setLink(r.data);
  };
  const saveHeadline = async () => {
    if (!link || headline === (link.headline || '')) return;
    const r = await audienceService.setLinkHeadline(ownerType, ownerId, headline);
    if (r.success) { setLink(r.data); setHeadlineSaved(true); setTimeout(() => setHeadlineSaved(false), 1800); }
  };
  const reqKey = link ? (link.requireEmail && link.requirePhone ? 'both' : link.requireEmail ? 'email' : link.requirePhone ? 'phone' : 'either') : 'either';

  // ── Groups ──
  const saveGroup = async () => {
    const name = (groupEdit?.name || '').trim();
    if (!name) return;
    const r = groupEdit.id ? await audienceService.renameGroup(ownerType, ownerId, groupEdit.id, name) : await audienceService.createGroup(ownerType, ownerId, name);
    if (!r.success) { await showAlert({ title: 'GROUPS', message: r.error || 'Could not save that.', type: 'error' }); return; }
    setGroupEdit(null); disarm();
    loadGroups();
  };
  const deleteGroup = async () => {
    const g = groups.find((x) => x.id === groupEdit?.id);
    if (!g || !confirm('del-group')) return;
    await audienceService.deleteGroup(ownerType, ownerId, g.id);
    if (groupId === g.id) setGroupId(null);
    setGroupEdit(null);
    refresh();
  };

  // ── Selection + bulk ──
  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOnPage = list?.items?.length > 0 && list.items.every((c) => selected.has(c.id));
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(list.items.map((c) => c.id)));
  const bulkGroup = async (gid, remove) => {
    if (!gid || selected.size === 0) return;
    setBusy(true);
    const r = await audienceService.setGroupMembers(ownerType, ownerId, gid, { contactIds: [...selected], remove });
    setBusy(false);
    if (!r.success) await showAlert({ title: 'GROUPS', message: r.error || 'Could not do that.', type: 'error' });
    else if (!remove) say(`${selected.size} ADDED TO ${String(groups.find((g) => g.id === gid)?.name || '').toUpperCase()}`);
    refresh();
  };
  const bulkDelete = async () => {
    if (!confirm('bulk-delete')) return;
    const n = selected.size;
    setBusy(true);
    await audienceService.deleteContacts(ownerType, ownerId, [...selected]);
    setBusy(false);
    say(`${n} REMOVED`);
    refresh();
  };
  const sendSelected = () => {
    const rows = (list?.items || []).filter((c) => selected.has(c.id));
    onSendTo?.({ userIds: rows.filter((c) => c.userId).map((c) => c.userId), contactIds: rows.map((c) => c.id) });
  };

  const stats = useMemo(() => {
    const counts = list?.counts || {};
    return [
      [counts.contacts || 0, 'CONTACTS'], [counts.withEmail || 0, 'WITH EMAIL'], [counts.withPhone || 0, 'WITH PHONE'], [counts.claimed || 0, 'ON PORTAL'], [counts.followers || 0, owner.followerWord || 'FOLLOWERS'],
    ];
  }, [list?.counts, owner.followerWord]);

  const r = mobile ? 22 : 24;
  const groupName = (id) => groups.find((g) => g.id === id)?.name;
  const filtered = !!(q || filter || groupId);

  return (
    <div className="flex flex-col font-mono" style={{ gap: mobile ? 10 : 16 }} data-testid="contacts-panel">
      {/* YOUR LINK */}
      <ClubPanel phone={mobile} style={{ gap: 16 }} data-testid="contact-link-panel">
        <PanelHead title="YOUR LINK" right={link ? `${Number(link.joinCount || 0).toLocaleString()} JOINED BY IT` : ''} />
        <div className="flex flex-wrap items-start" style={{ gap: 20 }}>
          <div className="flex min-w-0 flex-col" style={{ flex: '1 1 360px', gap: 14 }}>
            <span style={{ fontSize: 12, lineHeight: 1.65, color: BODY }}>
              Share it anywhere, or put the QR on the door. Whoever opens it drops a phone number or an email and lands on your list. When they make a Portal account with it, they {owner.followerWord === 'MEMBERS' ? 'join the club' : 'follow the page'} on their own.
            </span>
            <span className="flex items-center" style={{ gap: 6, padding: '6px 6px 6px 20px', borderRadius: 999, background: INSET }}>
              <span className="min-w-0 flex-1 truncate" style={{ fontSize: 14, fontWeight: 700 }} data-testid="contact-link-url">{link ? link.url.replace(/^https?:\/\//, '') : '…'}</span>
              <PillButton variant={copied ? 'cyan' : 'primary'} size={40} onClick={copy} disabled={!link} data-testid="copy-link">{copied ? 'COPIED' : 'COPY'}</PillButton>
            </span>
            <span className="flex flex-wrap" style={{ gap: 6 }}>
              <PillButton variant="tertiary" size={36} onClick={share} disabled={!link}>SHARE</PillButton>
              <PillButton variant="tertiary" size={36} onClick={() => link && window.open(link.url, '_blank', 'noopener')} disabled={!link}>OPEN</PillButton>
              <PillButton variant="tertiary" size={36} onClick={downloadQr} disabled={!link}>DOWNLOAD QR</PillButton>
              <PillButton variant={armed === 'rotate' ? 'armed' : 'dangerGhost'} size={36} onClick={() => (armed === 'rotate' ? disarm() : arm('rotate'))} disabled={!link} data-testid="new-link">NEW LINK</PillButton>
            </span>
            {armed === 'rotate' && (
              <div className="flex flex-wrap items-center" style={{ gap: 10, padding: '14px 16px', borderRadius: 18, background: RED_TINT }} data-testid="rotate-confirm">
                <span style={{ flex: '1 1 240px', fontSize: 12, lineHeight: 1.55, color: '#7F1D1D' }}>The old link and any QR you printed stop working the moment you do this. Everyone already on the list stays.</span>
                <span className="flex" style={{ gap: 6 }}>
                  <PillButton variant="armed" size={36} onClick={rotate}>MAKE A NEW LINK</PillButton>
                  <PillButton variant="ghost" size={36} onClick={disarm}>KEEP IT</PillButton>
                </span>
              </div>
            )}
            <label className="flex flex-col" style={{ gap: 8 }}>
              <span style={lbl(10, '.16em')}>LINE UNDER YOUR NAME ON THE JOIN PAGE</span>
              <span className="flex" style={{ gap: 6 }}>
                <input value={headline} onChange={(e) => { setHeadline(e.target.value); setHeadlineSaved(false); }} maxLength={160} placeholder="First dibs on every ticket." aria-label="Join page line" style={{ ...inputStyle, flex: 1, minWidth: 0 }} />
                <PillButton variant={headlineSaved ? 'cyan' : 'primary'} size={48} onClick={saveHeadline} disabled={!link} style={{ flexShrink: 0, opacity: headlineSaved || headline !== (link?.headline || '') ? 1 : 0.4 }}>{headlineSaved ? 'SAVED' : 'SAVE'}</PillButton>
              </span>
              <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>Optional. What they get for joining: first dibs on tickets, the members’ rate, the odd late-night text.</span>
            </label>
            <div className="flex flex-col" style={{ gap: 8 }}>
              <span style={lbl(10, '.16em')}>WHAT THEY HAVE TO GIVE</span>
              <span className="inline-flex flex-wrap self-start" style={{ gap: 2, padding: 3, borderRadius: 999, background: INSET }} role="radiogroup" aria-label="What a joiner has to give" data-testid="link-requirements">
                {[['either', 'EMAIL OR PHONE', false, false], ['email', 'EMAIL', true, false], ['phone', 'PHONE', false, true], ['both', 'BOTH', true, true]].map(([k, l, re, rp]) => {
                  const on = reqKey === k;
                  return <button key={k} type="button" role="radio" aria-checked={on} disabled={!link} onClick={() => setRequire(re, rp)} style={{ height: 38, padding: '0 16px', border: '2px solid transparent', borderRadius: 999, background: on ? INK : 'transparent', color: on ? '#fff' : INK, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', cursor: 'pointer' }}>{l}</button>;
                })}
              </span>
              <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>{REQ_NOTES[reqKey]}</span>
            </div>
          </div>
          <div className="flex flex-col items-center" style={{ flex: `0 0 ${mobile ? '100%' : '220px'}`, gap: 10 }}>
            <span className="flex" style={{ width: '100%', aspectRatio: '1', borderRadius: 22, background: INSET, padding: 18, boxSizing: 'border-box' }}>
              <span className="flex flex-1 items-center justify-center" style={{ borderRadius: 12, background: '#fff', padding: 12 }}>
                {link ? <BrandedQR value={link.url} size={mobile ? 180 : 160} /> : <span style={{ width: 160, height: 160 }} />}
              </span>
            </span>
            <span style={{ ...lbl(9, '.18em'), color: SUBTLE }}>SCAN TO JOIN</span>
          </div>
        </div>
      </ClubPanel>

      <div className="grid overflow-hidden" style={{ gridTemplateColumns: mobile ? 'repeat(2, minmax(0,1fr))' : 'repeat(5, minmax(0,1fr))', gap: 4, borderRadius: r }} data-testid="contact-stats">
        {stats.map(([n, k]) => (
          <span key={k} className="flex flex-col items-center bg-white text-center" style={{ gap: 6, padding: '18px 8px' }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>{Number(n).toLocaleString()}</span>
            <span style={{ ...lbl(9, '.14em'), color: MUTED }}>{k}</span>
          </span>
        ))}
      </div>

      {!importing && (
        <span className="flex flex-wrap" style={{ gap: 8 }}>
          <PillButton size={48} onClick={() => { setImporting(true); setAdding(false); }} data-testid="open-import">IMPORT A CSV / EXCEL</PillButton>
          <PillButton variant="secondary" size={48} onClick={() => setAdding((a) => !a)} data-testid="open-add">{adding ? 'CANCEL' : '+ ADD ONE BY HAND'}</PillButton>
        </span>
      )}
      {adding && !importing && <AddContact ownerType={ownerType} ownerId={ownerId} groups={groups} mobile={mobile} onAdded={() => { setAdding(false); say('ADDED TO THE LIST'); refresh(); }} />}
      {importing && <ImportSheet ownerType={ownerType} ownerId={ownerId} groups={groups} mobile={mobile} onClose={() => { setImporting(false); refresh(); }} onDone={() => { loadGroups(); }} />}

      {/* GROUPS */}
      <ClubPanel phone={mobile} data-testid="groups-panel">
        <span className="flex items-center justify-between" style={{ gap: 10 }}>
          <span style={lbl(12, '.16em')}>GROUPS</span>
          <PillButton variant="cyan" size={32} onClick={() => { setGroupEdit({ name: '' }); disarm(); }} data-testid="new-group">+ NEW GROUP</PillButton>
        </span>
        {groups.length === 0 && !groupEdit && <span style={{ fontSize: 12, lineHeight: 1.6, color: MUTED }}>Sort the list into groups (REGULARS, VIP, THURSDAY CROWD) and send to one at a time. A contact can be in several.</span>}
        <span className="flex flex-wrap" style={{ gap: 6 }}>
          <button type="button" aria-pressed={groupId === null} onClick={() => setGroupId(null)} style={{ ...chip(groupId === null), height: 38, padding: '0 16px' }}>EVERYONE<span style={{ opacity: 0.6 }}>{list ? (list.counts?.contacts ?? 0) : ''}</span></button>
          {groups.map((g) => {
            const on = groupId === g.id;
            return (
              <span key={g.id} className="inline-flex items-center" style={{ borderRadius: 999, background: on ? INK : CHIP, color: on ? '#fff' : INK }}>
                <button type="button" aria-pressed={on} onClick={() => setGroupId(on ? null : g.id)} style={{ ...chip(on), background: 'transparent', color: 'inherit', height: 38, padding: '0 6px 0 16px' }}>{String(g.name).toUpperCase()}<span style={{ opacity: 0.6 }}>{g.count}</span></button>
                <button type="button" aria-label={`Edit group ${g.name}`} onClick={() => { setGroupEdit({ id: g.id, name: g.name }); disarm(); }} style={{ height: 38, padding: '0 14px 0 4px', border: 'none', background: 'none', color: 'inherit', fontFamily: 'inherit', fontSize: 12, cursor: 'pointer' }}>✎</button>
              </span>
            );
          })}
        </span>
        {groupEdit && (
          <>
            <span className="flex flex-wrap items-center" style={{ gap: 6, padding: 6, borderRadius: 999, background: INSET }} data-testid="group-editor">
              <input value={groupEdit.name} onChange={(e) => setGroupEdit({ ...groupEdit, name: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') saveGroup(); }} maxLength={60} placeholder="Group name" aria-label="Group name" autoFocus style={{ flex: '1 1 180px', minWidth: 0, height: 40, padding: '0 16px', border: 'none', borderRadius: 999, background: '#fff', fontFamily: 'inherit', fontSize: 13, outline: 'none' }} />
              <PillButton size={40} onClick={saveGroup} disabled={!groupEdit.name.trim()} style={{ opacity: groupEdit.name.trim() ? 1 : 0.35 }}>{groupEdit.id ? 'RENAME' : 'CREATE'}</PillButton>
              {groupEdit.id && <PillButton variant={armed === 'del-group' ? 'armed' : 'dangerGhost'} size={40} onClick={deleteGroup} data-testid="delete-group">{armed === 'del-group' ? 'CONFIRM DELETE' : 'DELETE'}</PillButton>}
              <PillButton variant="ghost" size={40} weight={700} onClick={() => { setGroupEdit(null); disarm(); }} style={{ background: 'transparent' }}>CANCEL</PillButton>
            </span>
            {armed === 'del-group' && groupEdit.id && <span style={{ fontSize: 11, color: RED }}>The group goes; the {groups.find((g) => g.id === groupEdit.id)?.count ?? 0} contacts in it stay on the list.</span>}
          </>
        )}
      </ClubPanel>

      {/* THE LIST */}
      <ClubPanel phone={mobile} style={{ gap: 12 }} data-testid="contacts-list">
        <PanelHead title={groupId ? String(groupName(groupId) || 'GROUP').toUpperCase() : 'THE LIST'} right={list ? `${Number(list.total).toLocaleString()} ${list.total === 1 ? 'PERSON' : 'PEOPLE'}` : ''} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or number" aria-label="Search contacts" style={inputStyle} data-testid="contacts-search" />
        <span className="flex" style={{ gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {FILTERS.map((f) => <button key={f.key || 'all'} type="button" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)} style={{ ...chip(filter === f.key), height: 34 }}>{f.label}</button>)}
        </span>
        {selected.size > 0 && (
          <>
            <div className="flex flex-wrap items-center" style={{ gap: 8, padding: '8px 8px 8px 18px', borderRadius: 999, background: INK, color: '#fff' }} data-testid="bulk-bar">
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.12em' }}>{selected.size} SELECTED</span>
              {onSendTo && <PillButton variant="cyan" size={34} onClick={sendSelected} data-testid="send-to-these">SEND TO THESE →</PillButton>}
              {groups.filter((g) => g.id !== groupId).map((g) => <PillButton key={g.id} size={34} onClick={() => bulkGroup(g.id, false)} disabled={busy} style={{ background: '#262626' }}>+ {String(g.name).toUpperCase()}</PillButton>)}
              {groupId && <PillButton size={34} onClick={() => bulkGroup(groupId, true)} disabled={busy} style={{ background: '#262626' }}>REMOVE FROM GROUP</PillButton>}
              <PillButton variant={armed === 'bulk-delete' ? 'armed' : 'primary'} size={34} onClick={bulkDelete} disabled={busy} style={{ marginLeft: 'auto', background: armed === 'bulk-delete' ? RED : '#262626' }} data-testid="bulk-delete">{armed === 'bulk-delete' ? `REMOVE ${selected.size}?` : 'REMOVE FROM LIST'}</PillButton>
              <button type="button" aria-label="Clear selection" onClick={() => { setSelected(new Set()); disarm(); }} style={{ width: 34, height: 34, border: 'none', borderRadius: '50%', background: '#262626', color: '#fff', fontFamily: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>
            {armed === 'bulk-delete' && <span style={{ fontSize: 11, lineHeight: 1.55, color: RED }}>They come off your list. Anyone who is also a {owner.followerWord === 'MEMBERS' ? 'member stays a member' : 'follower stays a follower'}. This only removes the contact row.</span>}
          </>
        )}
        {list === null ? (
          <div className="py-6 text-center"><LoadingSpinner size="sm" /></div>
        ) : list.items.length === 0 ? (
          <div className="flex flex-col items-center text-center" style={{ gap: 8, padding: '44px 20px', borderRadius: 22, border: '2px dashed #D1D5DB' }} data-testid="contacts-empty">
            <span style={{ ...lbl(12, '.2em'), color: SUBTLE }}>{filtered ? 'NOBODY MATCHES.' : 'NOBODY ON THE LIST YET.'}</span>
            <span style={{ maxWidth: 360, fontSize: 13, lineHeight: 1.6, color: MUTED }}>{filtered ? 'Try a different search or filter.' : 'Share your link, put the QR on the door, or import a list you already have.'}</span>
          </div>
        ) : (
          <div className="flex flex-col">
            <span className="flex items-center self-start" style={{ gap: 12, padding: '6px 0 10px' }}>
              <RoundCheck on={allOnPage} onChange={toggleAll} label="Select all shown" testId="select-all" />
              <button type="button" onClick={toggleAll} style={{ ...lbl(9, '.14em'), color: MUTED, border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', cursor: 'pointer' }}>SELECT ALL SHOWN</button>
            </span>
            {list.items.map((c, i) => <ContactRow key={c.id} c={c} groups={groups} selected={selected.has(c.id)} onToggle={() => toggle(c.id)} tint={i} />)}
            {list.items.length < list.total && (
              <PillButton variant="tertiary" size={40} onClick={() => loadList({ page: page + 1 })} style={{ marginTop: 10 }}>SHOW MORE · {(list.total - list.items.length).toLocaleString()} LEFT</PillButton>
            )}
          </div>
        )}
      </ClubPanel>
      <Toast text={toast} phone={mobile} />
    </div>
  );
}

function ContactRow({ c, groups, selected, onToggle, tint }) {
  const name = c.name || c.email || c.phone || '—';
  const groupNames = (c.groupIds || []).map((id) => groups.find((g) => g.id === id)?.name).filter(Boolean);
  const onPortal = !!c.userId;
  return (
    <div className="flex flex-wrap items-center" style={{ gap: '8px 12px', padding: '11px 0', borderTop: RULE, background: selected ? '#F0FEFF' : 'transparent' }} data-testid="contact-row">
      <RoundCheck on={selected} onChange={onToggle} label={`Select ${name}`} />
      <Face src={c.userAvatarUrl} name={name} size={40} tintKey={tint} />
      <span className="flex min-w-0 flex-col" style={{ flex: '1 1 180px', gap: 4 }}>
        <span className="flex min-w-0 items-center" style={{ gap: 6 }}>
          <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{name}</span>
          {c.userHandle && <span style={{ fontSize: 10, color: MUTED }}>@{c.userHandle}</span>}
        </span>
        <span className="truncate" style={{ fontSize: 11, color: MUTED }}>
          {[c.email, c.phone].filter(Boolean).join(' · ') || '—'}
          {c.unsubscribed && <span style={{ color: RED }}> · UNSUBSCRIBED</span>}
          {!c.unsubscribed && c.smsOptedOut && <span style={{ color: '#B45309' }}> · NO TEXTS</span>}
        </span>
        {groupNames.length > 0 && <span className="flex flex-wrap" style={{ gap: 4 }}>{groupNames.map((g) => <span key={g} style={{ padding: '2px 8px', borderRadius: 999, background: CHIP, fontSize: 8, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase' }}>{g}</span>)}</span>}
      </span>
      <TagPill bg={onPortal ? INK : CHIP} fg={onPortal ? '#fff' : INK}>{onPortal ? 'ON PORTAL' : SOURCE_LABEL[c.source] || String(c.source || 'ADDED').toUpperCase()}</TagPill>
    </div>
  );
}

function AddContact({ ownerType, ownerId, groups, mobile, onAdded }) {
  const { showAlert } = useDialog();
  const [form, setForm] = useState({ name: '', email: '', phone: '', groupIds: [] });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ok = !!(form.email.trim() || form.phone.trim());
  const submit = async () => {
    if (!ok) return;
    setBusy(true);
    const r = await audienceService.addContact(ownerType, ownerId, { name: form.name, email: form.email, phone: form.phone, phoneCountry: 'CA', groupIds: form.groupIds });
    setBusy(false);
    if (!r.success) { await showAlert({ title: 'COULD NOT ADD', message: r.error || 'Check the email or number.', type: 'error' }); return; }
    onAdded?.(r.data);
  };
  const field = { height: 46, padding: '0 18px', border: 'none', borderRadius: 999, background: INSET, fontFamily: 'inherit', fontSize: 14, color: INK, outline: 'none', width: '100%', boxSizing: 'border-box' };
  return (
    <ClubPanel phone={mobile} data-testid="add-contact">
      <span style={lbl(12, '.16em')}>ADD ONE BY HAND</span>
      <div className="grid" style={{ gridTemplateColumns: mobile ? 'minmax(0,1fr)' : 'repeat(3, minmax(0,1fr))', gap: 10 }}>
        <label className="flex flex-col" style={{ gap: 6 }}><span style={lbl(10, '.16em')}>NAME</span><input value={form.name} onChange={(e) => set('name', e.target.value)} maxLength={120} aria-label="Name" style={field} /></label>
        <label className="flex flex-col" style={{ gap: 6 }}><span style={lbl(10, '.16em')}>EMAIL</span><input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} aria-label="Email" style={field} /></label>
        <label className="flex flex-col" style={{ gap: 6 }}><span style={lbl(10, '.16em')}>PHONE</span><input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} aria-label="Phone" style={field} /><span style={{ fontSize: 10, color: MUTED }}>+1 assumed unless it starts with +</span></label>
      </div>
      {groups.length > 0 && (
        <span className="flex flex-wrap items-center" style={{ gap: 6 }}>
          <span style={{ ...lbl(10, '.16em'), color: MUTED, paddingRight: 4 }}>GROUPS</span>
          {groups.map((g) => <button key={g.id} type="button" aria-pressed={form.groupIds.includes(g.id)} onClick={() => set('groupIds', form.groupIds.includes(g.id) ? form.groupIds.filter((x) => x !== g.id) : [...form.groupIds, g.id])} style={{ ...chip(form.groupIds.includes(g.id)), height: 32, letterSpacing: '.1em' }}>{String(g.name).toUpperCase()}</button>)}
        </span>
      )}
      <span className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <span style={{ fontSize: 11, color: MUTED }}>Only add someone who asked to hear from you.</span>
        <PillButton size={44} onClick={submit} disabled={busy || !ok} style={{ marginLeft: 'auto', opacity: ok ? 1 : 0.35 }} data-testid="add-contact-submit">{busy ? 'ADDING…' : 'ADD TO THE LIST'}</PillButton>
      </span>
    </ClubPanel>
  );
}
